import { useEffect, useRef } from 'react';
import useStakeholderNotesFunctions from 'components/Dashboard/StakeHolders/StakeholderPage/useStakeholderNotesFunctions';
import useCrmInteractionsQuery from 'components/Dashboard/Influence/CrmInteractions/useCrmInteractionsQuery';

// Loads the notes list and an interactions head-count when the stakeholder page
// mounts so the bio tab buttons can show entry counts without the user opening
// each tab first. Mirrors useStakeholderSentimentSnapshotApiCalls (the Activity
// tab sentiment dot). The notes hook instance is handed back so the Notes section
// reuses it instead of re-fetching; Groups reads its count straight off the
// stakeholder record.
const useStakeholderBioTabCounts = ({ StakeholderInformation, enabled }) => {
  const { objectId, id, groups, contactInformation } = StakeholderInformation ?? {};
  // Interactions are keyed by CrmContact.id (contactInformation[0]) — same fallback the
  // Interactions section uses when the stakeholder isn't a CRM contact yet.
  const crmContactId = contactInformation?.[0]?.id ?? id;

  const notesFunctions = useStakeholderNotesFunctions();
  const { userNotes, getNotes } = notesFunctions;

  const initialCall = useRef(null);
  initialCall.current = (source) => {
    if (!enabled) return;
    getNotes({ objectId, requestSource: source, contentType: 'Stakeholder' });
  };

  useEffect(() => {
    const source = new AbortController();
    initialCall.current(source);
    return () => {
      source.abort('Api call cancelled by the user');
    };
  }, [enabled]);

  // First page only (default pageSize 10) — enough to render the "9+" cap.
  const { interactions, isLoading: isLoadingInteractions } = useCrmInteractionsQuery({
    filters: [{ field: 'crmContactIds', operator: 'str_eq', value: String(crmContactId) }],
    sort: { field: 'createdAtUtc', order: 'DESC' },
    dependencyKey: `bio-tab-count-${crmContactId}`,
    enabled: enabled && !!crmContactId,
  });

  return {
    notesFunctions,
    notesCount: userNotes?.length ?? 0,
    interactionsCount: interactions?.length ?? 0,
    groupsCount: groups?.length ?? 0,
    // Both loaded — lets every tab reveal its count in one synced animation.
    countsReady: !enabled || (!notesFunctions.isLoading && !isLoadingInteractions),
  };
};

export default useStakeholderBioTabCounts;
